// src/components/resources/glossary/GlossaryNavigation.tsx

'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion } from 'motion/react'
import { cn } from '~/lib/utils'
import { getGlossaryCategoryTheme } from '~/lib/resources/glossary/category-ui'

// Nav-specific ordering (charts before general); the per-category color family
// comes from the shared theme SoT (`getGlossaryCategoryTheme(id).colorName`).
const categories = [
  { id: 'metrics', label: 'Marketing Metrics' },
  { id: 'creative', label: 'Creative Terms' },
  { id: 'platform', label: 'Platform Terms' },
  { id: 'charts', label: 'Charts' },
  { id: 'general', label: 'General' }
] as const

export function GlossaryNavigation(): JSX.Element {
  const pathname = usePathname()
  const currentCategory = pathname.split('/').pop()

  return (
    <div className="border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <nav className="flex space-x-4 py-4 overflow-x-auto">
          {categories.map(({ id, label }) => {
            const isActive = currentCategory === id
            const color = getGlossaryCategoryTheme(id).colorName
            const colorClasses = {
              indigo: 'hover:text-indigo-600 hover:bg-indigo-50 dark:hover:text-indigo-400 dark:hover:bg-indigo-900/20',
              purple: 'hover:text-purple-600 hover:bg-purple-50 dark:hover:text-purple-400 dark:hover:bg-purple-900/20',
              blue: 'hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-900/20',
              green: 'hover:text-green-600 hover:bg-green-50 dark:hover:text-green-400 dark:hover:bg-green-900/20',
              gray: 'hover:text-gray-900 hover:bg-gray-100 dark:hover:text-gray-100 dark:hover:bg-gray-800'
            }

            const activeColorClasses = {
              indigo: 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/20',
              purple: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/20',
              blue: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20',
              green: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20',
              gray: 'text-gray-900 bg-gray-100 dark:text-gray-100 dark:bg-gray-800'
            }

            return (
              <Link
                key={id}
                href={`/resources/glossary#${id}`}
                className={cn(
                  "relative flex-none rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                  isActive
                    ? activeColorClasses[color]
                    : cn(
                        "text-gray-600 dark:text-gray-400",
                        colorClasses[color]
                      )
                )}
              >
                {label}
                {isActive && (
                  <motion.div
                    layoutId="active-category"
                    className={cn(
                      "absolute inset-0 rounded-full mix-blend-multiply dark:mix-blend-overlay",
                      {
                        'bg-indigo-100 dark:bg-indigo-900/30': color === 'indigo',
                        'bg-purple-100 dark:bg-purple-900/30': color === 'purple',
                        'bg-blue-100 dark:bg-blue-900/30': color === 'blue',
                        'bg-green-100 dark:bg-green-900/30': color === 'green',
                        'bg-gray-200 dark:bg-gray-800': color === 'gray'
                      }
                    )}
                  />
                )}
              </Link>
            )
          })}
        </nav>
      </div>
    </div>
  )
}